import type { paths, ErrorResponse } from "@onsemble/shared";
import createClient from "openapi-fetch";
import {
  CONTRACTOR_ORG_HEADER,
  getActiveContractorScope,
} from "@/lib/contractor-scope";

export const client = createClient<paths>({
  credentials: "include",
});

// PC users acting for a contractor scope every request to that contractor
client.use({
  onRequest({ request }) {
    const contractorOrgId = getActiveContractorScope();
    if (contractorOrgId) {
      request.headers.set(CONTRACTOR_ORG_HEADER, contractorOrgId);
    }
    return request;
  },
});

/**
 * Standardize what kind of error Tanstack queries and mutations will return for unsuccessful requests.
 */
export class TanstackError extends Error {
  status: number;
  error: string | undefined;
  /** Parsed error response body, when the server returned one (the error
   * envelope: code, requestId, retryable, ...). Consumed by parseApiError. */
  body: unknown;
  constructor(status: number, error?: string, body?: unknown) {
    super(error);
    this.status = status;
    this.error = error;
    this.body = body;
  }
}

/**
 * Now all "onError" callbacks are typed as TanstackError.
 * Use "handleClientMethod" in all tanstack querys and mutations to automatically provide them.
 */
declare module "@tanstack/react-query" {
  interface Register {
    defaultError: TanstackError;
  }
}

/**
 * Interface for the result from the client that handleClientMethod expects.
 */
export interface ClientResult<T> {
  error?: ErrorResponse;
  data?: T;
  response: {
    status: number;
  };
}

/**
 * Wrapper around an openapi-fetch client fetch method to handle errors and return the data in a way that is compatible with Tanstack Query.
 */
export const handleClientMethod = async <T>(
  request: Promise<ClientResult<T>>,
): Promise<T> => {
  let result: ClientResult<T>;
  try {
    result = await request;
  } catch (e) {
    throw new TanstackError(0, "Network error");
  }
  if (result.error !== undefined) {
    throw new TanstackError(
      result.response.status,
      result.error.error,
      result.error,
    );
  }
  if (result.data === undefined) {
    if (result.response.status === 204) {
      return undefined as T;
    }
    throw new TanstackError(result.response.status, "No data returned");
  }
  return result.data;
};
